import React from "react";
import { motion } from "framer-motion";
import {
  Gem,
  Scale,
  Sparkles,
  Clock,
  ShieldCheck,
  Headphones,
} from "lucide-react";

const PILLARS = [
  {
    icon: Scale,
    n: "01",
    title: "Local Expertise",
    copy: "Former government liaisons and corporate legal specialists. We know which desks to approach — and in what order.",
  },
  {
    icon: Gem,
    n: "02",
    title: "Transparent Pricing",
    copy: "No hidden admin fees, no surprise add-ons. The quotation you receive is the number you pay — in writing, in advance.",
  },
  {
    icon: Sparkles,
    n: "03",
    title: "The Smart Approach",
    copy: "We don't just hand you a trade license. We architect a compliant, scalable corporate structure aligned to your 5-year plan.",
  },
  {
    icon: Clock,
    n: "04",
    title: "Speed, Discipline",
    copy: "Most engagements delivered in 4–6 weeks end-to-end. Weekly status reports and SLA-backed response times.",
  },
  {
    icon: ShieldCheck,
    n: "05",
    title: "Audit-Grade File",
    copy: "Every document — MoA, UBO, KYC — is prepared to banking and regulator standards from day one. Zero rework.",
  },
  {
    icon: Headphones,
    n: "06",
    title: "Lifetime Partnership",
    copy: "We stay with you post-incorporation — renewals, new activities, office moves, exit planning. You're not a ticket.",
  },
];

export default function WhyStartINC() {
  return (
    <section
      data-testid="why-section"
      id="why"
      className="relative bg-[#002366] text-white py-20 md:py-32 overflow-hidden"
    >
      <div className="absolute top-0 left-0 h-px w-full bg-gradient-to-r from-transparent via-[#3366FF]/60 to-transparent" />
      <div
        className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-[#3366FF]/10 blur-3xl pointer-events-none"
        aria-hidden
      />
      <div className="container-x relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-8">
            <p className="overline text-[#6B8AFF]">Why StartINC</p>
            <div className="gold-rule mt-5 mb-6" />
            <h2 className="font-heading font-light tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-tight">
              Built by people who have{" "}
              <span className="font-black text-[#6B8AFF]">sat on the other side</span> of the desk.
            </h2>
          </div>
          <p className="lg:col-span-4 text-slate-300/85 leading-relaxed">
            Our partners have spent years inside Dubai Economic Department, DMCC and
            UAE banks. That's why StartINC files get approved the first time.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10 rounded-sm overflow-hidden">
          {PILLARS.map(({ icon: Icon, n, title, copy }, i) => (
            <motion.div
              key={title}
              data-testid={`why-pillar-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.1 }}
              className="bg-[#002366] p-8 md:p-10 group hover:bg-[#001A4D] transition-colors duration-300 relative"
            >
              <div className="flex items-start justify-between mb-8">
                <div className="w-11 h-11 rounded-sm border border-white/15 inline-flex items-center justify-center text-[#6B8AFF] group-hover:border-[#3366FF]/70 group-hover:text-white transition-colors">
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <span className="font-heading font-black text-4xl text-white/10 group-hover:text-[#3366FF]/40 transition-colors">
                  {n}
                </span>
              </div>
              <h3 className="font-heading font-semibold text-lg md:text-xl tracking-tight">
                {title}
              </h3>
              <p className="mt-3 text-slate-300/85 text-sm md:text-base leading-relaxed">
                {copy}
              </p>
              <div className="mt-6 h-px w-10 bg-[#3366FF] transition-all duration-500 group-hover:w-24" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
